import { useEffect, useRef, useSyncExternalStore } from 'react';

// Module-level external store — Zendesk has no unsubscribe, so the listener
// is registered once and count is shared across all hook instances.
let _count = 0;
const _subscribeListeners = new Set<() => void>();
const _onCountCallbacks = new Set<(n: number) => void>();
let _registered = false;

function subscribe(listener: () => void): () => void {
  _subscribeListeners.add(listener);
  return () => {
    _subscribeListeners.delete(listener);
  };
}

function getSnapshot(): number {
  return _count;
}

function notifyAll(n: number): void {
  _count = n;
  for (const listener of _subscribeListeners) listener();
  for (const cb of _onCountCallbacks) cb(n);
}

function ensureRegistered(): void {
  if (_registered) return;
  _registered = true;

  const register = () => {
    window.zE?.('messenger:on', 'unreadMessages', (count: unknown) => {
      notifyAll(Number(count));
    });
  };

  if (window.zE) {
    register();
  } else {
    window.addEventListener('ze-loaded', register, { once: true });
  }
}

/**
 * Subscribes to the Zendesk messenger unread message count via `useSyncExternalStore`.
 *
 * @param onCount - Optional callback invoked directly when the count changes,
 *   so callers can react without a secondary `useEffect`.
 */
export function useZendeskUnreadMessages(onCount?: (count: number) => void): {
  unreadMessagesCount: number;
} {
  ensureRegistered();

  const onCountRef = useRef(onCount);
  onCountRef.current = onCount;

  useEffect(() => {
    const stableCallback = (n: number) => onCountRef.current?.(n);
    _onCountCallbacks.add(stableCallback);
    return () => {
      _onCountCallbacks.delete(stableCallback);
    };
  }, []);

  const unreadMessagesCount = useSyncExternalStore(subscribe, getSnapshot, () => 0);
  return { unreadMessagesCount };
}
